import { formatParisDateTime, type PennylaneSettingsSummary } from '@flux/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type JSX } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { Button } from '@/components/ui/button.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import { Switch } from '@/components/ui/switch.js';
import { isSameValue } from '@/lib/is-same-value.js';
import { toast } from '@/lib/toast.js';
import { pennylaneQueryKeys, testPennylaneConnection, updatePennylaneSettings } from '@/features/pennylane/api.js';

function dailyTime(cron: string): string | null {
  const match = /^(\d{1,2}) (\d{1,2}) \* \* \*$/.exec(cron);
  return match ? `${match[2]!.padStart(2, '0')}:${match[1]!.padStart(2, '0')}` : null;
}

/** One activation switch; the shared advanced switch owns simulation. */
export function PennylaneSettingsSection({ settings }: { settings: PennylaneSettingsSummary }): JSX.Element {
  const queryClient = useQueryClient();
  const [keyDraft, setKeyDraft] = useState('');
  const [timeDraft, setTimeDraft] = useState(dailyTime(settings.schedule) ?? '');
  const [carrierCode, setCarrierCode] = useState(settings.defaultCarrier?.code ?? '');
  const [carrierLabel, setCarrierLabel] = useState(settings.defaultCarrier?.label ?? '');
  useEffect(() => { setTimeDraft(dailyTime(settings.schedule) ?? ''); }, [settings.schedule]);
  const invalidate = (): void => { void queryClient.invalidateQueries({ queryKey: pennylaneQueryKeys.all }); };
  const scheduleBase = {
    mode: 'active' as const,
    apiUrl: settings.apiUrl,
    catchupDays: settings.catchupDays,
    importFlag: settings.importFlag,
    defaultCarrier: settings.defaultCarrier,
  };
  const base = { ...scheduleBase, schedule: settings.schedule };
  const save = useMutation({
    mutationFn: updatePennylaneSettings,
    onSuccess: () => { toast.success('Réglages Pennylane enregistrés.'); setKeyDraft(''); invalidate(); },
    onError: (error: unknown) => toast.fromError(error),
  });
  const test = useMutation({
    mutationFn: testPennylaneConnection,
    onSuccess: (result) => result.ok ? toast.success(result.message) : toast.error(result.message),
    onError: (error: unknown) => toast.fromError(error),
  });
  const [hour, minute] = timeDraft.split(':').map(Number);
  const carrierInput = carrierCode.trim() ? { code: carrierCode.trim(), label: carrierLabel.trim() || carrierCode.trim() } : null;
  // Inactifs tant que rien n'a changé : le retour à l'état inactif après un clic confirme la sauvegarde.
  const scheduleUnchanged = timeDraft === (dailyTime(settings.schedule) ?? '');
  const carrierUnchanged = isSameValue(carrierInput, settings.defaultCarrier);
  return (
    <div className="flex flex-col gap-6">
      <PageSection title="Pennylane" description="Récupération et transmission des factures vers Martelet.">
        <div className="flex flex-col gap-5">
          <label className="flex items-center gap-3 text-sm font-medium">
            <Switch checked={settings.enabled} disabled={save.isPending} onCheckedChange={(enabled) => save.mutate({ ...base, enabled })} />
            Canal {settings.enabled ? 'actif' : 'inactif'}
          </label>
          <p className="text-xs text-muted-foreground">Un canal inactif ne récupère aucune nouvelle facture. Utilisez le rattrapage après sa réactivation.</p>
          <Field label={`Clé API ${settings.apiKeyConfigured ? '(configurée)' : '(à configurer)'}`} htmlFor="pennylane-key">
            <div className="flex flex-wrap gap-2">
              <Input id="pennylane-key" type="password" value={keyDraft} placeholder={settings.apiKeyConfigured ? '••••••••' : ''}
                onChange={(event) => setKeyDraft(event.target.value)} className="max-w-sm" />
              <Button type="button" variant="outline" disabled={!keyDraft.trim()} isLoading={save.isPending}
                onClick={() => save.mutate({ ...base, enabled: settings.enabled, apiKey: keyDraft.trim() })}>Enregistrer la clé</Button>
              <Button type="button" variant="outline" isLoading={test.isPending} onClick={() => test.mutate()}>Tester la connexion</Button>
            </div>
          </Field>
        </div>
      </PageSection>
      <PageSection title="Planification" description={`Actuellement : ${settings.scheduleDescription}. Les horaires existants sont conservés jusqu’à leur modification.`}>
        <div className="flex flex-col gap-4">
          <Field label="Chaque jour à" htmlFor="pennylane-time">
            <Input id="pennylane-time" type="time" value={timeDraft} className="w-40" onChange={(event) => setTimeDraft(event.target.value)} />
          </Field>
          {!dailyTime(settings.schedule) ? <p className="text-xs text-muted-foreground">La planification actuelle est conservée. Choisissez une heure pour la remplacer par une exécution quotidienne.</p> : null}
          <p className="text-xs text-muted-foreground">Prochaines exécutions : {settings.nextRuns.length ? settings.nextRuns.map((iso) => formatParisDateTime(new Date(iso))).join(', ') : 'aucune'}</p>
          <Button type="button" disabled={scheduleUnchanged || !timeDraft || !Number.isInteger(hour) || !Number.isInteger(minute)} isLoading={save.isPending}
            onClick={() => save.mutate({ ...scheduleBase, enabled: settings.enabled, preset: { kind: 'daily', hour: hour!, minute: minute! } })}>Enregistrer la planification</Button>
        </div>
      </PageSection>
      <PageSection title="Transporteur par défaut" description="Laisser vide pour laisser Martelet choisir ; une règle transporteur prioritaire reste applicable.">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Code" htmlFor="pennylane-carrier-code"><Input id="pennylane-carrier-code" value={carrierCode} onChange={(event) => setCarrierCode(event.target.value)} /></Field>
          <Field label="Nom" htmlFor="pennylane-carrier-label"><Input id="pennylane-carrier-label" value={carrierLabel} onChange={(event) => setCarrierLabel(event.target.value)} /></Field>
          <Button type="button" disabled={carrierUnchanged} isLoading={save.isPending} onClick={() => save.mutate({
            ...base, enabled: settings.enabled, defaultCarrier: carrierInput,
          })}>Enregistrer</Button>
        </div>
      </PageSection>
    </div>
  );
}
